import { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SectionHeader from 'shared/components/SectionHeader';
import { Utils } from 'shared/helpers';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import { PublicService } from 'services/PublicService';

export interface DatasetType {
  _id: string;
  datasetName: string;
  owningTeam: string;
  contactPerson: string;
  purposeOfUse: string;
  lifecycleStage: string;
  sourceType: string;
  dataSensitivityClassification: string;
  approvalStatus: string;
  riskTier: string;
  watermarkingTechnique: string;
  sourceDetails: string;
  dataCollectionMethod: string;
  geographicOrigin: string;
  volumeSize: string;
  retentionPolicy: string;
  accessControls: string;
  encryption: string;
  sharingRestrictions: string;
  dataDictionaryLink: string;
  metadataDescription: string;
  knownDataQualityIssues: string;
  validationSummary: string;
  metadataAvailable: boolean;
  datasheetAvailable: boolean;
  dataModalities: string[];
  sensitiveDataTypesPresent: string[];
  complianceTags: any[];
  downstreamDependencies: string;
  reviewFrequency: string;
  monitoringInPlace: boolean;
  tenantId: any;
  organizationId: string;
  createdBy: any;
  updatedBy: any;
  updatedDate: any;
  isActive: boolean;
  deletedAt: any;
  createdDate: string;
  __v: number;
}

const Dataset = (): ReactElement => {
  const [dataSetList, setDataSetList] = useState<DatasetType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getDataSets = (): void => {
    setIsLoading(true);
    PublicService.getDatasets('approved')
      .then((res) => {
        const { data } = res.data;
        setDataSetList(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getDataSets();
  }, []);

  return (
    <Paper variant="outlined" className="p-4">
      <SectionHeader
        icon={StorageIcon}
        title={`Approved Datasets${
          dataSetList.length ? ` (${dataSetList.length})` : ''
        }`}
        subtitle=" Datasets approved for use in AI development and operations"
        titleVariant="h3"
      />
      {dataSetList.length === 0 && isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {dataSetList.length > 0 && !isLoading && (
        <Grid container spacing={3} className="mt-3">
          {dataSetList.map((dataset, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card className="dataset-card">
                <Typography className="dataset-card-title">
                  {dataset.datasetName}
                </Typography>

                <Chip
                  label={dataset.sourceType}
                  size="small"
                  className="dataset-card-chip"
                />

                <Grid container spacing={2} className="dataset-card-content">
                  <Grid item xs={12} sm={12} className="dataset-card-row">
                    <GroupsIcon className="dataset-card-icon" />
                    <Box>
                      <Typography className="dataset-card-label">
                        Owning Team
                      </Typography>
                      <Typography className="dataset-card-value">
                        {dataset.owningTeam}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={12} className="dataset-card-row">
                    <PersonIcon className="dataset-card-icon" />
                    <Box>
                      <Typography className="dataset-card-label">
                        Contact Person
                      </Typography>
                      <Typography className="dataset-card-value">
                        {dataset.contactPerson}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={12} className="dataset-card-row">
                    <TimelineIcon className="dataset-card-icon" />
                    <Box>
                      <Typography className="dataset-card-label">
                        Lifecycle Stage
                      </Typography>
                      <Typography className="dataset-card-value">
                        {dataset.lifecycleStage}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      {!isLoading && dataSetList.length === 0 && (
        <Box className="empty-content">
          <Typography>No approved datasets found.</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default Dataset;
